import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import clsx from 'clsx';
import { withCartContext } from '../cart/cart-context';
import CartItem from '../cart/cart-item';
import Panel from '../utils/panel';
import { formatMoney, elevarTracking } from '../utils/helpers';
import CloseIcon from '../utils/icons/close.svg';
import parseCartItems from '../utils/parse-cart-items';
import { trackViewCart } from '../product-customizer/components/util/dataLayerEvents';
import Govx from '../utils/govx';
import ProductRecommendations from './product-recommendations';

const Minicart = (props) => {
  const { productRecommendations, productRecommendationsHeading } = props.data;

  const { cart, cartLoading } = props;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    'cart',
  );
  const lines = parseCartItems(cart?.state?.lines || []);

  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });

  // commented out until we build lifesharp
  // const [lifeSharpActive, setLifeSharpActive] = useState(true);
  const [isOpen, setIsOpen] = useState(isCartRedirect);

  const hasPersonalizedItem = !!lines.find((item) =>
    item.attributes.find((attr) => attr.key === '_threekit_id'),
  );

  useEffect(() => {
    if (isOpen && !cartLoading) {
      trackViewCart(cart.state);

      const products = cart.state.lines.map((line, index) => ({
        id: line.merchandise.sku,
        name: line.merchandise.product.title,
        brand: line.merchandise.product.vendor,
        category: line.merchandise.product.productType,
        variant: line.merchandise.title,
        price: `${line.merchandise.priceV2.amount}`,
        quantity: `${line.quantity}`,
        product_id: line.merchandise.product.id.split('/Product/')[1],
        variant_id: line.merchandise.id.split('/ProductVariant/')[1],
        compare_at_price: line.merchandise.compare_at_price
          ? `${line.merchandise.compare_at_price}`
          : '0.00',
        position: index + 1,
      }));

      elevarTracking({
        customerId: props.data.customerId,
        products,
        event: 'dl_view_cart',
        cartTotal: cart.state.subtotal.amount,
      });
    }
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [cartLoading, isOpen]);

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      context="cart"
      classes={'h-full w-[304px] tablet:w-[375px]'}
      showBackdrop={true}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div className="flex flex-col h-full">
        <div className="flex justify-between py-[22px] pr-6 pl-[18px] border-b border-grey-300 laptop:py-[27px] laptop:px-0 laptop:mr-6 laptop:ml-5">
          <h4 className="heading-5">{window.TMW.Strings.cart.title}</h4>
          <button type="button" ref={closeTriggerEl}>
            <CloseIcon />
          </button>
        </div>
        <div
          className={clsx(
            'px-[18px] laptop:px-5 h-auto overflow-auto',
            !lines?.length ? 'grow' : 'flex flex-col grow',
          )}
        >
          {!lines?.length && (
            <div className="flex items-center justify-center h-[65%]">
              <div class="-mt-24 flex justify-center items-center flex-col laptop:-mt-32 ">
                <h5 className="subheading-1 text-secondary-1 mb-3 laptop:mb-3 laptop:text-[24px]">
                  {window.TMW.Strings.cart.empty}
                </h5>
                {props.data.continue_link && props.data.continue_text && (
                  <a
                    className="underline uppercase font-m-12 text-primary-1"
                    href={props.data.continue_link}
                  >
                    {props.data.continue_text}
                  </a>
                )}
              </div>
            </div>
          )}
          {lines?.map((line) => (
            <CartItem
              key={line.id}
              line={line}
              isCartPage={false}
              data={props.data}
            />
          ))}
          {productRecommendations.length > 0 && (
            <ProductRecommendations
              productRecommendations={productRecommendations}
              productRecommendationsHeading={productRecommendationsHeading}
              customerId={props.data.customerId}
            />
          )}
        </div>
        <div
          className={`bg-bg-6 py-5 w-full ${lines?.length ? 'pt-2.5' : 'pt-0'}`}
        >
          {!!lines?.length && (
            <div className="flex justify-between items-center py-3.5 px-5 laptop:pt-0.5 laptop:pb-2">
              <p className="pr-6 basis-2/3 heading-7">{props.data.id_header}</p>
              <a
                href={props.data.id_account_link}
                className="text-right underline basis-1/3 font-m-10 text-primary-1"
              >
                {props.data.id_cta_text}
              </a>
            </div>
          )}

          <div className="px-5 border-t border-grey-400">
            <div className="flex justify-between items-center w-full mb-2.5 pt-[13px] laptop:mb-4 laptop:border-0">
              <h6 className="font-r-12 text-primary-1">
                {window.TMW.Strings.cart.subtotal}
              </h6>

              {lines?.length ? (
                <span className="font-r-12 text-primary-1">
                  {formatMoney(cart.state.subtotal)}
                </span>
              ) : (
                <span>&#8722;</span>
              )}
            </div>
            <button
              className={`btn btn-primary-fill w-full h-10 p-0 laptop:h-[50px] ${
                !lines?.length
                && 'disabled:bg-grey-500 disabled:border-grey-500 disabled:text-white'
              }`}
              disabled={!lines?.length ? 'disabled' : false}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = cart.state.checkoutUrl;
              }}
            >
              {window.TMW.Strings.cart.checkout}
            </button>
            {!!lines?.length && (
              <p className="mt-2 text-center font-r-12 text-secondary-1">
                {window.TMW.Strings.cart.taxes}
              </p>
            )}
            {hasPersonalizedItem && window.Benchmade.leadTimeMessaging && (
              <p class="mt-2 text-center font-r-12 text-secondary-1">
                {window.Benchmade.leadTimeMessaging ?? ''}
              </p>
            )}
            <Govx />
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default withCartContext(Minicart);
